import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import { getPageSEO } from '../lib/seo-config';
import { 
  User, 
  Bell, 
  Shield, 
  Trash2,
  Save,
  CreditCard,
  ExternalLink,
  Calendar,
  Check,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Mail,
  Upload,
  X,
} from 'lucide-react';
import { useAuth, useUser } from '../contexts/AuthContext';
import { CustomerPortalService } from '../lib/customer-portal-service';
import { supabase } from '../lib/supabase';
import { ProfileService } from '../lib/profile-service';
import { AvatarService } from '../lib/avatar-service';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AvatarCropModal } from '../components/AvatarCropModal';


interface EmailPreference {
  id: string;
  key: string;
  title: string;
  description: string;
  enabled: boolean;
}

export function SettingsPage() {
  const { user, logout } = useAuth();
  const { refreshUserProfile } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('profile');
  const [emailPreferences, setEmailPreferences] = useState<EmailPreference[]>([
    {
      id: 'project_notifications',
      key: 'project_notifications',
      title: 'Project Notifications',
      description: 'Get notified about project activities like analysis completion and team updates',
      enabled: true
    },
    {
      id: 'usage_alerts',
      key: 'usage_alerts',
      title: 'Usage Alerts',
      description: 'Receive alerts when your credits are running low or storage is nearly full',
      enabled: true
    },
    {
      id: 'marketing_emails',
      key: 'marketing_emails',
      title: 'Marketing & Updates',
      description: 'Receive product updates, tips, and promotional offers',
      enabled: false
    },
    {
      id: 'activity_digest',
      key: 'activity_digest',
      title: 'Weekly Activity Digest',
      description: 'Get a weekly summary of your team\'s activities and performance',
      enabled: false
    }
  ]);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  
  // Profile management state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: '',
    job_title: '',
    bio: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showAvatarCrop, setShowAvatarCrop] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Account deletion state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Notification state
  const [notification, setNotification] = useState<string | null>(null);
  
  const navigate = useNavigate();

  // Helper function to show notifications
  const showNotification = (message: string) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  // Handle URL parameters on component mount
  useEffect(() => {
    const tab = searchParams.get('tab');
    const portal = searchParams.get('portal');
    
    // Set active tab based on URL parameter
    if (tab && ['profile', 'notifications', 'security', 'billing'].includes(tab)) {
      setActiveTab(tab);
    }
    
    // Clean up URL by removing the portal parameter if it exists
    if (portal === 'success') {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('portal');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);


  // Update profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        company: user.company || '',
        job_title: user.job_title || '',
        bio: user.bio || ''
      });
    }
  }, [user]);

  const loadEmailPreferences = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoadingPreferences(true);
      
      // Try to get existing preferences first
      const { data, error } = await supabase
        .from('email_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle(); // Use maybeSingle() instead of single()

      if (error) {
        console.error('Error loading email preferences:', error);
        return;
      }

      if (data) {
        // User has existing preferences
        setEmailPreferences(prev => prev.map(pref => ({
          ...pref,
          enabled: (data as Record<string, unknown>)[pref.key] as boolean ?? pref.enabled
        })));
      } else {
        // No preferences exist, create default ones
        const defaultPreferences = {
          user_id: user.id,
          project_notifications: true,
          usage_alerts: true,
          marketing_emails: false,
          activity_digest: false,
          updated_at: new Date().toISOString()
        };

        const { error: insertError } = await supabase
          .from('email_preferences')
          .insert(defaultPreferences);

        if (insertError) {
          console.error('Error creating default email preferences:', insertError);
        } else {
          // Update local state with defaults
          setEmailPreferences(prev => prev.map(pref => ({
            ...pref,
            enabled: Boolean((defaultPreferences as Record<string, boolean | string>)[pref.key] ?? pref.enabled)
          })));
        }
      }
    } catch (error) {
      console.error('Error loading email preferences:', error);
    } finally {
      setLoadingPreferences(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && activeTab === 'notifications') {
      loadEmailPreferences();
    }
  }, [user, activeTab, loadEmailPreferences]);

  const toggleNotification = async (id: string) => {
    const preference = emailPreferences.find(p => p.id === id);
    if (!preference || !user?.id) return;

    const newValue = !preference.enabled;

    // Update local state immediately for better UX
    setEmailPreferences(prev =>
      prev.map(pref =>
        pref.id === id ? { ...pref, enabled: newValue } : pref
      )
    );

    // Update in database
    try {
      const { error } = await supabase
        .from('email_preferences')
        .upsert({
          user_id: user.id,
          [preference.key]: newValue,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error updating email preference:', error);
        // Revert local state on error
        setEmailPreferences(prev =>
          prev.map(pref =>
            pref.id === id ? { ...pref, enabled: preference.enabled } : pref
          )
        );
        return;
      }

      // If marketing_emails preference changed, sync with Resend
      if (preference.key === 'marketing_emails' && user.email) {
        try {
          const { EmailService } = await import('../lib/email-service');

          if (newValue) {
            // Add to Resend
            await EmailService.addToResend(
              user.email,
              user.name?.split(' ')[0],
              user.name?.split(' ').slice(1).join(' ') || undefined,
              user.id
            );
            console.log('✅ User added to Resend marketing audience');
          } else {
            // Remove from Resend
            await EmailService.removeFromResend(user.email, user.id);
            console.log('✅ User removed from Resend marketing audience');
          }
        } catch (resendError) {
          console.error('Error syncing with Resend:', resendError);
          // Don't revert - preference was updated successfully in DB
          // Just show a warning
          alert('Email preference updated, but there was an issue syncing with our marketing system. Your preference has been saved.');
        }
      }
    } catch (error) {
      console.error('Error updating email preference:', error);
    }
  };

  const saveAllPreferences = async () => {
    if (!user?.id) return;

    try {
      setSavingPreferences(true);
      
      const preferencesData = emailPreferences.reduce((acc, pref) => ({
        ...acc,
        [pref.key]: pref.enabled
      }), {});

      const { error } = await supabase
        .from('email_preferences')
        .upsert({
          user_id: user.id,
          ...preferencesData,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving email preferences:', error);
        alert('Failed to save preferences. Please try again.');
      }
    } catch (error) {
      console.error('Error saving email preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSavingPreferences(false);
    }
  };

  // Profile management functions
  const handleProfileSave = async () => {
    if (!user?.id) return;

    try {
      setSavingProfile(true);

      // Validate data
      if (!ProfileService.isValidName(profileData.name)) {
        alert('Please enter a valid name (2-100 characters)');
        return;
      }

      if (!ProfileService.isValidEmail(profileData.email)) {
        alert('Please enter a valid email address');
        return;
      }

      // Update profile
      await ProfileService.updateProfile(user.id, profileData);
      
      // Refresh user context
      await refreshUserProfile();
      
      showNotification('Profile updated successfully!');
    } catch (error: unknown) {
      console.error('Profile update error:', error);
      alert((error instanceof Error ? error.message : 'Unknown error') || 'Failed to update profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }


    // Create image URL for cropping
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = e.target?.result?.toString() || '';
      
      // Set the image data and show modal
      setCropImageSrc(imageData);
      setShowAvatarCrop(true);
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarCropComplete = async (croppedFile: File) => {
    if (!user?.id) return;

    try {
      setUploadingAvatar(true);
      setShowAvatarCrop(false);
      setCropImageSrc('');
      
      await ProfileService.updateAvatar(user.id, croppedFile, user.avatar || undefined);
      
      // Refresh user context
      await refreshUserProfile();
      
    } catch (error: unknown) {
      console.error('❌ Avatar upload error:', error);
      alert((error instanceof Error ? error.message : 'Unknown error') || 'Failed to upload avatar. Please try again.');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAvatarCropCancel = () => {
    setShowAvatarCrop(false);
    setCropImageSrc('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePasswordChange = async () => {
    try {
      setChangingPassword(true);
      await ProfileService.changePassword(passwordData);
      
      // Clear form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      showNotification('Password changed successfully!');
    } catch (error: unknown) {
      console.error('Password change error:', error);
      alert((error instanceof Error ? error.message : 'Unknown error') || 'Failed to change password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type "DELETE" to confirm account deletion');
      return;
    }

    if (!user?.id) return;

    try {
      setDeletingAccount(true);
      
      console.log('🚨 [DEBUG] SettingsPage: Starting account deletion for user:', user.id);
      
      // Show initial progress
      showNotification('Starting account deletion process...');
      
      console.log('🚨 [DEBUG] SettingsPage: About to call ProfileService.deleteAccount');
      
      // Call the comprehensive delete function
      await ProfileService.deleteAccount(user.id);
      
      console.log('🚨 [DEBUG] SettingsPage: ProfileService.deleteAccount completed successfully');
      
      // Show success message
      showNotification('Account deleted successfully. Signing out...');
      
      // Wait a moment for the message, then logout properly
      setTimeout(async () => {
        try {
          // Use the proper logout method to clear all app state
          await logout();
          // Navigate to homepage after logout
          navigate('/');
        } catch (logoutError) {
          console.error('Logout error after account deletion:', logoutError);
          // Force navigation even if logout fails
          navigate('/');
        }
      }, 1500);
      
    } catch (error: unknown) {
      console.error('Account deletion error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Show specific error in notification instead of alert
      showNotification(`Account deletion failed: ${errorMessage}`);
      
      // Also show detailed error for debugging
      if (errorMessage.includes('Stripe') || errorMessage.includes('subscription')) {
        alert(`Account deletion encountered an issue with your subscription: ${errorMessage}\n\nPlease contact support at support@rulevia.com if this persists.`);
      } else if (errorMessage.includes('storage') || errorMessage.includes('files')) {
        alert(`Account deletion encountered an issue with file cleanup: ${errorMessage}\n\nYour account data has been deleted, but some files may remain. Contact support if needed.`);
      } else {
        alert(`Failed to delete account: ${errorMessage}\n\nPlease try again or contact support at support@rulevia.com if the problem persists.`);
      }
    } finally {
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'billing', label: 'Billing', icon: CreditCard }
  ];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <SEOHead {...getPageSEO('/settings')} />
      {/* Notification */}
      {notification && (
        <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-4 right-4 left-4 sm:left-auto max-w-sm z-50 bg-green-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2"
          >
            <Check className="w-5 h-5" />
            <span>{notification}</span>
          </motion.div>
      )}
      
      <div className="mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Manage your account preferences and application settings.
          </p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
        {/* Sidebar Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 h-fit"
        >
          <nav className="space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left min-h-[44px] ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 border border-purple-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <tab.icon className={`w-5 h-5 ${
                  activeTab === tab.id ? 'text-purple-600' : 'text-gray-500'
                }`} />
                <span className="font-medium text-sm sm:text-base">{tab.label}</span>
              </button>
            ))}
          </nav>
        </motion.div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          {activeTab === 'profile' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6"
            >
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Profile Information</h2>
              
              <div className="space-y-4 sm:space-y-6">
                {/* Avatar */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start sm:space-x-6 space-y-4 sm:space-y-0">
                  <img
                    src={AvatarService.getAvatarUrl(
                      user?.avatar ?? null, 
                      user?.name || undefined, 
                      user?.email || undefined
                    )}
                    alt={user?.name}
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                  />
                  <div className="text-center sm:text-left">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleAvatarSelect}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <button 
                      onClick={() => {
                        fileInputRef.current?.click();
                      }}
                      disabled={uploadingAvatar}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-h-[44px]"
                    >
                      {uploadingAvatar ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          <span>Change Avatar</span>
                        </>
                      )}
                    </button>
                    <p className="text-xs sm:text-sm text-gray-600 mt-2">JPG, PNG, GIF, or WebP (max. 5MB)</p>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={profileData.name}
                      onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      value={profileData.company}
                      onChange={(e) => setProfileData(prev => ({ ...prev, company: e.target.value }))}
                      placeholder="Your company name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Title
                    </label>
                    <input
                      type="text"
                      value={profileData.job_title}
                      onChange={(e) => setProfileData(prev => ({ ...prev, job_title: e.target.value }))}
                      placeholder="Your job title"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bio
                  </label>
                  <textarea
                    rows={4}
                    value={profileData.bio}
                    onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell us about yourself..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="flex justify-end">
                  <button 
                    onClick={handleProfileSave}
                    disabled={savingProfile}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] w-full sm:w-auto"
                  >
                    {savingProfile ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              
            </motion.div>
          )}

          {activeTab === 'profile' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-xl border border-red-200 p-4 sm:p-6 mt-6"
            >
              <h2 className="text-lg sm:text-xl font-semibold text-red-800 mb-4 sm:mb-6 flex items-center">
                <Trash2 className="w-5 sm:w-6 h-5 sm:h-6 mr-3" />
                Danger Zone
              </h2>
              
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-medium text-red-800 mb-2 text-sm sm:text-base">Delete Account</h3>
                  <p className="text-red-700 mb-4 text-sm">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <button 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors min-h-[44px] text-sm font-medium"
                  >
                    Delete Account
                  </button>
                </div>
                
                {/* Delete confirmation modal */}
                {showDeleteConfirm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-4 sm:p-6 max-w-md w-full">
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <h3 className="text-base sm:text-lg font-semibold text-red-800">Delete Account</h3>
                        <button 
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmText('');
                          }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="mb-4 sm:mb-6">
                        <p className="text-gray-700 mb-3 sm:mb-4 text-sm font-medium">
                          ⚠️ This action cannot be undone. This will permanently delete your account and all associated data including:
                        </p>
                        <ul className="text-gray-600 text-xs sm:text-sm space-y-1 ml-4">
                          <li>• All your projects and creative files</li>
                          <li>• Analysis results and reports</li>
                          <li>• Team memberships and collaborations</li>
                          <li>• Subscription and billing history</li>
                          <li>• Purchased credits and premium features</li>
                        </ul>
                        
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-700 text-xs sm:text-sm font-medium mb-2">
                            🔄 Automatic Actions:
                          </p>
                          <ul className="text-red-600 text-xs space-y-1 ml-4">
                            <li>• Active subscriptions will be canceled immediately</li>
                            <li>• All files will be permanently deleted from our servers</li>
                            <li>• You will receive a confirmation email</li>
                            <li>• This process may take up to 2 minutes to complete</li>
                          </ul>
                        </div>
                        
                        <div className="mt-3 sm:mt-4">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                            Type <span className="font-bold text-red-600">DELETE</span> to confirm:
                          </label>
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent min-h-[44px]"
                            placeholder="Type DELETE here"
                          />
                        </div>
                      </div>
                      
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmText('');
                          }}
                          className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] text-sm sm:text-base"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteAccount}
                          disabled={deletingAccount || deleteConfirmText !== 'DELETE'}
                          className="flex-1 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 min-h-[44px] text-sm sm:text-base"
                        >
                          {deletingAccount ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>Deleting Account...</span>
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              <span>Delete Account Forever</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'notifications' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-4 sm:space-y-6"
            >
              {/* Email Preferences */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Email Preferences</h2>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Control which emails you receive from Rulevia</p>
                  </div>
                  <Mail className="w-5 sm:w-6 h-5 sm:h-6 text-gray-400" />
                </div>
                
                {loadingPreferences ? (
                  <div className="flex items-center justify-center h-32">
                    <RefreshCw className="w-6 h-6 text-purple-600 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {emailPreferences.map((preference) => (
                      <div key={preference.id} className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg hover:border-purple-200 transition-colors">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 text-sm sm:text-base">{preference.title}</h3>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">{preference.description}</p>
                        </div>
                        <button
                          onClick={() => toggleNotification(preference.id)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                            preference.enabled ? 'bg-purple-600' : 'bg-gray-200'
                          }`}
                          style={{ minWidth: '2.75rem' }}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              preference.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                    
                    <div className="flex justify-end pt-4">
                      <button
                        onClick={saveAllPreferences}
                        disabled={savingPreferences}
                        className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] w-full sm:w-auto"
                      >
                        {savingPreferences ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            <span>Save All Preferences</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Email Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">Important Email Information</h3>
                    <p className="text-blue-800 text-xs sm:text-sm mb-2">
                      Some emails are always sent regardless of your preferences:
                    </p>
                    <ul className="text-blue-800 text-xs sm:text-sm space-y-1 ml-4">
                      <li>• Payment confirmations and receipts</li>
                      <li>• Failed payment notifications</li>
                      <li>• Account security alerts</li>
                      <li>• Team invitations</li>
                    </ul>
                    <p className="text-blue-800 text-xs sm:text-sm mt-3">
                      You can unsubscribe from all marketing emails at any time by clicking the unsubscribe link in any email.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'security' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-4 sm:space-y-6"
            >
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Security Settings</h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-4">Change Password</h3>
                    <div className="space-y-3 sm:space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Password
                        </label>
                        <input
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]"
                        />
                      </div>
                      <button 
                        onClick={handlePasswordChange}
                        disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-h-[44px]"
                      >
                        {changingPassword ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Updating...</span>
                          </>
                        ) : (
                          <span>Update Password</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <h3 className="font-medium text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Two-Factor Authentication</h3>
                <p className="text-gray-600 mb-3 sm:mb-4 text-sm">Add an extra layer of security to your account</p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  <p className="text-blue-800 text-xs sm:text-sm">
                    Two-factor authentication is coming soon. We're working on implementing this feature to enhance your account security.
                  </p>
                </div>
              </div>

            </motion.div>
          )}


          {activeTab === 'billing' && (
            <BillingTab />
          )}
        </div>
      </div>
      
      {/* Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-4 right-4 left-4 sm:left-auto max-w-sm z-50 bg-green-500 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2"
        >
          <Check className="w-5 h-5" />
          <span>{notification}</span>
        </motion.div>
      )}
      
      {/* Avatar Crop Modal */}
      <AvatarCropModal
        isOpen={showAvatarCrop}
        imageSrc={cropImageSrc}
        onClose={handleAvatarCropCancel}
        onCropComplete={handleAvatarCropComplete}
      />
    </div>
  );
}

function BillingTab() {
  const { user } = useAuth();
  const [subscriptionContext, setSubscriptionContext] = useState<{
    hasAccess: boolean;
    planName?: string;
    status?: string;
    nextBillingDate?: string;
    cancelAtPeriodEnd?: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  const loadSubscriptionData = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const context = await CustomerPortalService.getSubscriptionContext(user.id);
      setSubscriptionContext({
        hasAccess: context.hasAccess,
        planName: context.planName ?? undefined,
        status: context.status ?? undefined,
        nextBillingDate: context.nextBillingDate ?? undefined,
        cancelAtPeriodEnd: context.cancelAtPeriodEnd
      });
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadSubscriptionData();
  }, [user, loadSubscriptionData]);

  const handleManageSubscription = async () => {
    if (!user?.id) return;
    
    try {
      setRedirecting(true);
      await CustomerPortalService.redirectToPortal(user.id);
    } catch (error) {
      console.error('Error opening customer portal:', error);
      alert('Unable to open billing portal. Please try again or contact support.');
    } finally {
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6"
      >
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-4 sm:space-y-6"
      >
      {/* Current Subscription */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Current Subscription</h2>
        
        <div className="space-y-4">
          {subscriptionContext?.hasAccess ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 space-y-3 sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm sm:text-base">
                      {subscriptionContext.planName || 'Active Plan'}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Status: <span className="capitalize">{subscriptionContext.status}</span>
                    </p>
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  {subscriptionContext.nextBillingDate && (
                    <p className="text-xs sm:text-sm text-gray-600">
                      Next billing: {new Date(subscriptionContext.nextBillingDate).toLocaleDateString()}
                    </p>
                  )}
                  {subscriptionContext.cancelAtPeriodEnd && (
                    <p className="text-xs sm:text-sm text-orange-600 font-medium">
                      <AlertCircle className="w-3 sm:w-4 h-3 sm:h-4 inline mr-1" />
                      Cancels at period end
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-4">
                <button
                  onClick={handleManageSubscription}
                  disabled={redirecting}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] w-full sm:w-auto"
                >
                  {redirecting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Opening Portal...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      <span>Manage Subscription</span>
                      <ExternalLink className="w-4 h-4" />
                    </>
                  )}
                </button>
                <p className="text-xs sm:text-sm text-gray-600 mt-2">
                  Update payment methods, view billing history, and manage your subscription.
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="p-3 bg-gray-100 rounded-full w-fit mx-auto mb-3 sm:mb-4">
                <AlertCircle className="w-6 sm:w-8 h-6 sm:h-8 text-gray-500" />
              </div>
              <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">No Active Subscription</h3>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm">
                You don't have an active subscription. Subscribe to access premium features.
              </p>
              <button 
                onClick={() => window.location.href = '/onboarding'}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all min-h-[44px]"
              >
                View Plans
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Billing Information */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Billing Information</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-gray-500" />
              <div>
                <h3 className="font-medium text-gray-900 text-sm sm:text-base">Billing Cycle</h3>
                <p className="text-xs sm:text-sm text-gray-600">Monthly subscription billing</p>
              </div>
            </div>
            {subscriptionContext?.hasAccess && (
              <button
                onClick={handleManageSubscription}
                className="text-purple-600 hover:text-purple-700 text-xs sm:text-sm font-medium"
              >
                View History
              </button>
            )}
          </div>

          {subscriptionContext?.hasAccess ? (
            <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <CreditCard className="w-5 h-5 text-gray-500" />
                <div>
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base">Payment Method</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Manage your payment methods</p>
                </div>
              </div>
              <button
                onClick={handleManageSubscription}
                className="text-purple-600 hover:text-purple-700 text-xs sm:text-sm font-medium"
              >
                Update
              </button>
            </div>
          ) : (
            <div className="p-3 sm:p-4 border border-gray-200 rounded-lg bg-gray-50">
              <p className="text-gray-600 text-center text-sm">
                No billing information available. Subscribe to a plan to manage billing.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">Secure Billing Portal</h3>
            <p className="text-blue-800 text-xs sm:text-sm">
              All billing operations are handled securely through Stripe's customer portal. 
              Your payment information is never stored on our servers and is protected by 
              industry-standard encryption.
            </p>
          </div>
        </div>
      </div>

    </motion.div>
    </>
  );
}

